import {
  Facility,
  FacilityRequest,
  FacilityType,
  FacilityUpdate,
  HealthcareFacilityStats,
} from "@/types/facility.definition";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { facilitiesApi } from "../../services/facilities.api";
import { searchApi } from "../../services/search.api";
import { Order, SearchRequest } from "../../types/search.definition";
import {
  createFacilityFailure,
  createFacilityRequest,
  createFacilitySuccess,
  fetchFacilityByIdFailure,
  fetchFacilityByIdRequest,
  fetchFacilityByIdSuccess,
  fetchFacilityStatsFailure,
  fetchFacilityStatsRequest,
  fetchFacilityStatsSuccess,
  searchFacilitiesFailure,
  searchFacilitiesRequest,
  searchFacilitiesSuccess,
  updateFacilityFailure,
  updateFacilityRequest,
  updateFacilitySuccess,
} from "../reducers/facilitySlice";

// Sagas
function* searchFacilitiesSaga(action: {
  payload: {
    keyword?: string;
    offset?: number;
    limit?: number;
    filters?: {
      facilityType?: string;
      status?: string;
      isPartner?: string;
    };
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  };
}) {
  try {
    const {
      keyword = "",
      offset = 0,
      limit = 12,
      filters = {},
      sortBy = "name",
      sortOrder = "asc",
    } = action.payload;

    // Always use the requested limit for consistent pagination
    const actualLimit = limit;

    // Build query object with filters
    const query: {
      keyword: string;
      facilityType?: FacilityType;
      disabled?: boolean;
      isPartner?: boolean;
    } = {
      keyword,
    };

    // Add filters to query if they exist
    if (filters.facilityType) {
      query.facilityType = filters.facilityType as FacilityType;
    }
    if (filters.status) {
      // MED-1463: the BFF QueryVM takes a boolean `disabled`, not a status
      // string. "inactive" means disabled; leaving the field out means
      // "no status filter".
      query.disabled = filters.status === "inactive";
    }
    if (filters.isPartner) {
      query.isPartner = filters.isPartner === "partner";
    }

    const searchRequest: SearchRequest = {
      paginationSettings: {
        offset,
        limit: actualLimit,
      },
      sortBy: {
        property: sortBy,
        order: sortOrder === "asc" ? Order.ASC : Order.DESC,
      },
      query,
    };

    const response: {
      data: Facility[];
      paginationSettings: { totalCount: number; offset: { current: number } };
    } = yield call(searchApi.searchHealthFacilities, searchRequest);

    const hasMore =
      response.paginationSettings.offset.current + limit <
      response.paginationSettings.totalCount;

    // Always use searchFacilitiesSuccess for consistent behavior
    yield put(
      searchFacilitiesSuccess({
        facilities: response.data,
        totalCount: response.paginationSettings.totalCount,
        hasMore,
      })
    );
  } catch (error) {
    // Always use searchFacilitiesFailure for consistent behavior
    yield put(
      searchFacilitiesFailure(
        error instanceof Error ? error.message : "Failed to search facilities"
      )
    );
  }
}

function* fetchFacilityByIdSaga(action: { payload: string }) {
  try {
    const facility: Facility = yield call(
      facilitiesApi.getFacilityById,
      parseInt(action.payload)
    );
    yield put(fetchFacilityByIdSuccess(facility));
  } catch (error) {
    yield put(
      fetchFacilityByIdFailure(
        error instanceof Error ? error.message : "Failed to fetch facility"
      )
    );
  }
}

function* fetchFacilityStatsSaga(action: { payload: string }) {
  try {
    const stats: HealthcareFacilityStats = yield call(
      facilitiesApi.fetchFacilityStats,
      parseInt(action.payload)
    );
    yield put(fetchFacilityStatsSuccess(stats));
  } catch (error) {
    yield put(
      fetchFacilityStatsFailure(
        error instanceof Error
          ? error.message
          : "Failed to fetch facility stats"
      )
    );
  }
}

function* createFacilitySaga(action: { payload: FacilityRequest }) {
  try {
    const newFacility: Facility = yield call(
      facilitiesApi.createFacility,
      action.payload
    );
    yield put(createFacilitySuccess(newFacility));
  } catch (error) {
    yield put(
      createFacilityFailure(
        error instanceof Error ? error.message : "Failed to create facility"
      )
    );
  }
}

function* updateFacilitySaga(action: {
  payload: {
    facilityId: string;
    data: FacilityUpdate;
    onSuccess?: () => void;
    onError?: () => void;
  };
}) {
  const { facilityId, data, onSuccess, onError } = action.payload;
  try {
    const updatedFacility: Facility = yield call(
      facilitiesApi.updateFacility,
      parseInt(facilityId),
      data
    );
    yield put(updateFacilitySuccess(updatedFacility));
    if (onSuccess) yield call(onSuccess);
  } catch (error) {
    yield put(
      updateFacilityFailure(
        error instanceof Error ? error.message : "Failed to update facility"
      )
    );
    if (onError) yield call(onError);
  }
}

// Root saga
export function* facilitySaga() {
  yield takeLatest(searchFacilitiesRequest, searchFacilitiesSaga);
  yield takeLatest(fetchFacilityByIdRequest, fetchFacilityByIdSaga);
  yield takeLatest(fetchFacilityStatsRequest, fetchFacilityStatsSaga);
  yield takeEvery(createFacilityRequest, createFacilitySaga);
  yield takeEvery(updateFacilityRequest, updateFacilitySaga);
}
